import type { IProgressModuleFields } from "@types-contentful";
import React, { useState, useEffect } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import styled from "styled-components";
import "react-circular-progressbar/dist/styles.css";
import Icon from "@components/Icon";
import { colours, fonts, getColours } from "@helpers";
import { easeQuadInOut } from "d3-ease";
import { timer } from "d3-timer";
import { useInView } from "react-intersection-observer";
import CTAButton from "./CTAButton";

const Container = styled.div<{
	resetModuleMarginBottom: "No" | "Yes" | undefined;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 45px;
  padding: 56px 10px;
  @media (max-width: 834px) {
    gap: 32px;
    flex-direction: column;
    padding: 56px 35px;
  }
  .CircularProgressbar {
    width: 128px;
  }
  .CircularProgressbar-text {
    font-size: ${fonts.P};
  }
  h3 {
    margin-bottom: 8px;
  }
  img {
    @media (max-width: 834px) {
      display: none;
    }
  }
`;
const AchievedAndProgressBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  @media (max-width: 834px) {
    flex-direction: column;
  }
`;
const AchievedSection = styled.section`
  display: flex;
  flex-direction: column;
  margin-right: 50px;
  @media (max-width: 834px) {
    margin-bottom: 12px;
	margin-right: 0px;
  }
`;

const GoalSection = styled.section`
  margin-left: 50px;
  @media (max-width: 834px) {
    margin-top: 12px;
	margin-left: 0px;
  }
`;

function numberWithCommas(x: number) {
	return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const ProgressModule = ({
	achievedNumber,
	ctaButtonColour,
	ctaButtonLabel,
	ctaButtonUrl,
	goalNumber,
	iconRef,
	name,
	progressBarColour,
	removeNegativeSpacingBelowModule,
	subCopy,
	variableCounted,
}: IProgressModuleFields) => {
	const [ref, inView] = useInView({ triggerOnce: true });
	const [percentage, setPercentage] = useState(0);

	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;

	const targetPercentage = Math.round((achievedNumber / goalNumber) * 100);

	const GetProgressBarColour = getColours(progressBarColour);
	const progressBarTrailColour =
		progressBarColour === "Green"
			? `${colours.MINT_GREEN}`
			: `${colours.PALE_PEACH}`;

	useEffect(() => {
		if (inView) {
			const duration = 2.5 * 1000;
			const ease = easeQuadInOut;

			const t = timer((elapsed) => {
				const progress = elapsed / duration;
				if (progress > 1) {
					t.stop();
					setPercentage(targetPercentage);
				} else {
					setPercentage(Math.round(targetPercentage * ease(progress)));
				}
			});

			return () => t.stop();
		}
	}, [inView, targetPercentage]);

	return (
		<Container resetModuleMarginBottom={resetModuleMarginBottom} ref={ref}>
			<Icon
				iconName={iconRef}
				iconSizes={{
					iconMobile: 60,
					iconTablet: 60,
					iconLaptop: 60,
					iconDesktop: 60,
					iconDesktopLarge: 60,
				}}
				alt={iconRef}
			/>
			<AchievedAndProgressBarContainer>
				<AchievedSection>
					<span>
						<h3>
							{numberWithCommas(achievedNumber)} {variableCounted}
						</h3>
					</span>
					{subCopy}
				</AchievedSection>
				<CircularProgressbar
					value={percentage}
					text={`${percentage}%`}
					strokeWidth={16}
					styles={buildStyles({
						pathTransition: "none",
						textColor: `${colours.MIDNIGHT_BLUE}`,
						pathColor: `${GetProgressBarColour}`,
						trailColor: `${progressBarTrailColour}`,
					})}
				/>
				<GoalSection>
					<h3>
						{numberWithCommas(goalNumber)} {variableCounted}
					</h3>
					<p>is our goal</p>
				</GoalSection>
			</AchievedAndProgressBarContainer>
			{ctaButtonUrl && ctaButtonColour && ctaButtonLabel && (
				<CTAButton
					url={ctaButtonUrl}
					ctaButtonColour={ctaButtonColour}
					gtmCtaPosition={name || "no position provided in contentful"}
					gtmCtaText={ctaButtonLabel}
				>
					{ctaButtonLabel}
				</CTAButton>
			)}
		</Container>
	);
};

export default ProgressModule;
